import { createTheme, ThemeOptions, Theme, PaletteColor, PaletteColorOptions} from '@mui/material/styles';
import aquanowDarkPalette from './__generated__/palette.aquanowDark';
import aquanowLightPalette from './__generated__/palette.aquanowLight';

declare module '@mui/material/styles' {
  interface Mixins {
    iconAvatar: {
      width: number;
      height: number;
    };
    pageHeaderSpacing: object;
    pageHeaderSpacingTop: object;
    pageHeaderSpacingBottom: object;
    pageFooterSpacing: object;
    pageContentSpacing: object;
  }

  interface Palette {
    navbar?: PaletteColor;        // readable via theme.palette.navbar
  }
  interface PaletteOptions {
    navbar?: PaletteColorOptions; // allowed when you build the theme
  }

  interface MixinsOptions {
    iconAvatar?: {
      width: number;
      height: number;
    };
    pageHeaderSpacing?: object;
    pageHeaderSpacingTop?: object;
    pageHeaderSpacingBottom?: object;
    pageFooterSpacing?: object;
    pageContentSpacing?: object;
  }
  
  interface TypographyVariants {
    buttonSmall: React.CSSProperties;
    buttonLarge: React.CSSProperties;
    label: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    buttonSmall?: React.CSSProperties;
    buttonLarge?: React.CSSProperties;
    label?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    buttonSmall: true;
    buttonLarge: true;
    label: true;
  }
}

const baseThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontWeight: '600',
      fontSize: '4rem',
      lineHeight: '6rem',
    },
    h2: {
      fontWeight: '600',
      fontSize: '3rem',
      lineHeight: '4.5rem',
    },
    h3: {
      fontWeight: '600',
      fontSize: '2.5rem',
      lineHeight: '3.75rem',
    },
    h4: {
      fontWeight: '600',
      fontSize: '2rem',
      lineHeight: '3rem',
    },
    h5: {
      fontWeight: '600',
      fontSize: '1.5rem',
      lineHeight: '2.25rem',
    },
    h6: {
      fontWeight: '600',
      fontSize: '1.25rem',
      lineHeight: '2rem',
    },
    subtitle1: {
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
    subtitle2: {
      fontWeight: '500',
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
    body1: {
      fontWeight: '500',
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
    body2: {
      fontWeight: '500',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    label: {
      fontWeight: '600',
      fontSize: '0.75rem',
      lineHeight: '1rem',
    },
    caption: {
      fontWeight: '500',
      fontSize: '0.75rem',
      lineHeight: '1rem',
    },
    buttonSmall: {
      fontWeight: '600',
      fontSize: '0.75rem',
      lineHeight: '1.5rem',
    },
    button: {
      fontWeight: '600',
      fontSize: '0.875rem',
      lineHeight: '1.5rem',
      textTransform: 'none',
    },
    buttonLarge: {
      fontWeight: '600',
      fontSize: '1rem',
      lineHeight: '1.5rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
};

// Shared theme configuration function
const createBaseTheme = (palette: any) => {
  const baseTheme = createTheme({
    ...baseThemeOptions,
    palette,
  });
  
  return createTheme({
    ...baseTheme,
    mixins: {
      ...baseTheme.mixins,
      toolbar: {
        minHeight: 56,
        maxHeight: 56,
        [baseTheme.breakpoints.up('md')]: {
          '@media (orientation: landscape)': {
            minHeight: 48,
            maxHeight: 48,
          },
        },
        [baseTheme.breakpoints.up('md')]: {
          minHeight: 64,
          maxHeight: 64,
        },
      },
      iconAvatar: {
        width: 48,
        height: 48,
      },
      pageHeaderSpacing: {
        [baseTheme.breakpoints.down('md')]: {
          padding: baseTheme.spacing(3, 2),
        },
        [baseTheme.breakpoints.up('md')]: {
          padding: baseTheme.spacing(3),
        },
      },
      pageHeaderSpacingTop: {
        [baseTheme.breakpoints.down('md')]: {
          padding: baseTheme.spacing(3, 2, 0),
        },
        [baseTheme.breakpoints.up('md')]: {
          padding: baseTheme.spacing(3, 3, 0),
        },
      },
      pageHeaderSpacingBottom: {
        [baseTheme.breakpoints.down('md')]: {
          padding: baseTheme.spacing(0, 2, 3),
        },
        [baseTheme.breakpoints.up('md')]: {
          padding: baseTheme.spacing(0, 3, 3),
        },
      },
      pageFooterSpacing: {
        [baseTheme.breakpoints.down('md')]: {
          padding: baseTheme.spacing(2),
        },
        [baseTheme.breakpoints.up('md')]: {
          padding: baseTheme.spacing(2, 3),
        },
      },
      pageContentSpacing: {
        [baseTheme.breakpoints.down('md')]: {
          padding: baseTheme.spacing(3, 0),
        },
        [baseTheme.breakpoints.up('md')]: {
          padding: baseTheme.spacing(3),
        },
      },
    },
    components: {
      // Add CssBaseline component to set HTML background color
      MuiCssBaseline: {
        styleOverrides: (theme) => {
          return {
            html: {
              backgroundColor: theme.palette.background.default,
              transition: 'background-color 0.3s ease',
            },
            body: {
              backgroundColor: 'inherit',
              transition: 'background-color 0.3s ease',
            }
          };
        },
      },
      // Add Stepper component customization for better contrast
      MuiStepIcon: {
        styleOverrides: {
          root: ({ theme }) => ({
            // Improve active step styling
            '&.Mui-active': {
              color: theme.palette.primary.main,
              '& .MuiStepIcon-text': {
                fill: theme.palette.primary.contrastText,
                fontWeight: 600,
              },
            },
            // Improve completed step styling
            '&.Mui-completed': {
              color: theme.palette.success.main,
            },
            // Improve the text within the step icon
            '& .MuiStepIcon-text': {
              fill: theme.palette.mode === 'dark' 
                ? theme.palette.common.white
                : theme.palette.common.black,
              fontWeight: 500,
            },
          }),
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: ({ theme }) => ({
            // Improve label contrast
            color: theme.palette.text.secondary,
            // Improve active label styling
            '&.Mui-active': {
              color: theme.palette.text.primary,
              fontWeight: 600,
            },
            // Improve completed label styling
            '&.Mui-completed': {
              color: theme.palette.text.primary,
            },
          }),
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: ({ theme }) => ({
            // Add subtle background to make the stepper stand out
            backgroundColor: theme.palette.mode === 'dark' 
              ? theme.palette.background.default 
              : theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(1, 0),
          }),
        },
      },
      MuiStep: {
        styleOverrides: {
          root: ({ theme }) => ({
            // Add spacing between steps
            padding: theme.spacing(0, 1),
          }),
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          disableElevation: true,
          disableRipple: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: ({ theme, ownerState }: { theme: Theme; ownerState: { 
            color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'inherit';
            variant?: 'text' | 'outlined' | 'contained';
          }
         }) => ({
            borderRadius: theme.shape.borderRadius,
            lineHeight: '24px',
            textTransform: 'none',
            backgroundColor: theme.palette.primary.main, 
            color: "#FFFFFF" ,
            ...(ownerState.color &&
              ownerState.color !== 'inherit' &&
              ownerState.variant === 'contained' && {
                '&:active': {
                  backgroundColor: theme.palette[ownerState.color].dark,
                },
              }),
            ...(ownerState.color &&
              ownerState.color !== 'inherit' &&
              ownerState.variant === 'outlined' && {
                '&:active': {
                  borderColor: theme.palette[ownerState.color]?.dark,
                  backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
                },
              }),
            ...(ownerState.variant === 'text' && {
              '&:hover,&:focus-visible': {
                backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 100 : 900],
              },
              '&:active': {
                backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
              },
            }),
            '&:focus-visible': {
              outlineWidth: 2,
              outlineColor: theme.palette.action.focus,
              outlineStyle: 'solid',
              outlineOffset: 0,
              boxShadow: `0 0 0 4px ${theme.palette.action.focus}`,
            },
          }),
          sizeSmall: ({ theme }: { theme: Theme }) => ({
            fontSize: theme.typography.pxToRem(12),
            height: 32,
            padding: '4px 8px',
          }),
          sizeMedium: ({ theme }: { theme: Theme }) => ({
            fontSize: theme.typography.pxToRem(14),
            height: 40,
            padding: '8px 12px',
          }),
          sizeLarge: ({ theme }: { theme: Theme }) => ({
            fontSize: theme.typography.pxToRem(16),
            height: 48,
            padding: '12px 16px',
          }),
        },
        variants: [
          {
            props: { variant: 'text' },
            style: ({ theme }: { theme: Theme }) => ({
              '&:hover,&:focus-visible': {
                backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 100 : 900],
              },
              '&:active': {
                backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
              },
            }),
          },
        ],
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            label: {
              position: 'relative',
              transform: 'none',
              color: theme.palette.text.primary,
              marginBottom: '8px',
              ...theme.typography.label,
            },
          }),
        },
        defaultProps: {
          InputLabelProps: { shrink: false },
        },
      },
    },
  });
};

// Simplified theme creation functions
export const createLightTheme = () => createBaseTheme(aquanowLightPalette);

export const createDarkTheme = () => createBaseTheme(aquanowDarkPalette);